import { Fragment } from "react";

import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface FormShellSkeletonProps {
  /** `wizard` (Card com stepper) espelha o `WizardShell`; `sectioned` (tabs) o `SectionedShell`. */
  variant?: "wizard" | "sectioned";
  /** Quantos grupos de campos esboçar (só no `sectioned`). */
  sectionCount?: number;
  /** Quantos campos por grupo. */
  fieldCount?: number;
}

/** Grade de campos: rótulo + input, no mesmo layout responsivo das seções reais. */
function FieldGridSkeleton({ fieldCount = 6 }: { fieldCount?: number }) {
  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: fieldCount }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-10 w-full" />
        </div>
      ))}
    </div>
  );
}

/** Cabeçalho de seção: título + subtítulo. */
function HeadingSkeleton() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-3.5 w-64 max-w-full" />
    </div>
  );
}

/**
 * Esqueleto de carregamento das cascas de formulário. Em vez de um spinner solto,
 * desenha o próprio layout (passos/abas + cabeçalho + campos) com shimmer — o
 * usuário já vê a forma do que está chegando ("as seções estão carregando").
 * Agnóstico de domínio, como o resto da casca.
 */
export function FormShellSkeleton({
  variant = "wizard",
  sectionCount = 2,
  fieldCount = 6,
}: FormShellSkeletonProps) {
  if (variant === "sectioned") {
    return (
      <div
        aria-busy="true"
        aria-label="Carregando formulário"
        className="duration-300 animate-in fade-in"
      >
        {/* Tabs de navegação */}
        <div className="mb-2 flex gap-4 border-b border-border pb-3">
          {Array.from({ length: sectionCount + 1 }).map((_, i) => (
            <Skeleton key={i} className="h-5 w-24" />
          ))}
        </div>

        <div className="divide-y divide-border">
          {Array.from({ length: sectionCount }).map((_, i) => (
            <div key={i} className="space-y-6 py-8 first:pt-6">
              <HeadingSkeleton />
              <FieldGridSkeleton fieldCount={fieldCount} />
            </div>
          ))}
        </div>

        {/* Rodapé */}
        <div className="mt-8 flex items-center justify-between border-t border-border pt-5">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-9 w-36" />
        </div>
      </div>
    );
  }

  return (
    <Card
      aria-busy="true"
      aria-label="Carregando formulário"
      className="border border-border bg-card shadow-sm duration-300 animate-in fade-in"
    >
      {/* Stepper */}
      <CardHeader className="border-b border-border bg-muted/5 px-4 py-5 sm:px-6">
        {/* Mobile: título + barra segmentada */}
        <div className="space-y-2 md:hidden">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-20" />
          </div>
          <div className="flex gap-1.5">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-1.5 flex-1" />
            ))}
          </div>
        </div>
        {/* Desktop: bolinhas + conectores */}
        <div className="hidden items-center md:flex">
          {Array.from({ length: 3 }).map((_, i) => (
            <Fragment key={i}>
              <div className="flex items-center gap-3">
                <Skeleton className="size-9" circle />
                <Skeleton className="h-4 w-24" />
              </div>
              {i < 2 && <Skeleton className="mx-3 h-0.5 flex-1" />}
            </Fragment>
          ))}
        </div>
      </CardHeader>

      <CardContent className="space-y-6 px-4 pb-8 pt-6 sm:px-6">
        <HeadingSkeleton />
        <FieldGridSkeleton fieldCount={fieldCount} />
      </CardContent>

      <CardFooter className="flex items-center justify-between border-t border-border bg-muted/10 px-4 py-4 sm:px-6">
        <Skeleton className="h-9 w-24" />
        <Skeleton className="h-9 w-32" />
      </CardFooter>
    </Card>
  );
}
